import { match, P } from 'ts-pattern';

// Format time to display like "10:00 am"
export function formatTime(date: string | Date): string {
    return new Date(date)
        .toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
        })
        .toLowerCase();
}

// Format time to display like "10:00 AM"
export function formatClockTime(date: string | Date): string {
    return formatTime(date).toUpperCase();
}

// Format a time range. Drops the start meridiem when both ends share it
// (e.g. "12:15 – 12:17 pm"); keeps both when they differ ("11:55 am – 12:17 pm").
export function formatTimeRange(start: Date | string, end: Date | string): string {
    const startTime = formatTime(start);
    const endTime = formatTime(end);
    const startMeridiem = startTime.slice(-2);
    const endMeridiem = endTime.slice(-2);

    if (startMeridiem === endMeridiem) {
        return `${startTime.slice(0, -3)} – ${endTime}`;
    }
    return `${startTime} – ${endTime}`;
}

// Format a date range. Drops the start year when both ends share it
// (e.g. "Sep 3 – Oct 2, 2025"); keeps both when they differ
// ("Dec 15, 2025 – Jan 14, 2026"). Pass timeZone to format date-only
// boundaries that must not shift with the viewer's zone.
export function formatDateRange(
    start: Date | string,
    end: Date | string,
    options?: { timeZone?: string },
): string {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const timeZone = options?.timeZone;

    const year = new Intl.DateTimeFormat('en-US', { year: 'numeric', timeZone });
    const withYear = new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone,
    });
    const withoutYear = new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        timeZone,
    });

    const startLabel =
        year.format(startDate) === year.format(endDate)
            ? withoutYear.format(startDate)
            : withYear.format(startDate);

    return `${startLabel} – ${withYear.format(endDate)}`;
}

export function formatDurationLabel(durationMinutes: number): string {
    return `${durationMinutes} min`;
}

export function formatDateTime(date: Date | string): string {
    return new Date(date)
        .toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
        })
        .replace(/([AP]M)$/, (match) => match.toLowerCase());
}

// Calculate time difference between two dates and format as "Xh Ym" or "Ym"
export function getTimeBetween(endTime: string | Date, startTime: string | Date): string {
    const endTimeMs = new Date(endTime).getTime();
    const startTimeMs = new Date(startTime).getTime();
    const diffMs = startTimeMs - endTimeMs;

    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 0) {
        return `${hours}h ${minutes}m`;
    } else {
        return `${minutes}m`;
    }
}

export function convertMillisecondsToSeconds(ms: number): number {
    return Math.floor(ms / 1000);
}

export function convertMillisecondsToMinutes(ms: number): number {
    return Math.floor(ms / (1000 * 60));
}

export function formatNumberToTimeFormat(ms: number): string {
    const s = Math.floor(ms / 1000);
    const hours = Math.floor(s / 3600);
    const minutes = Math.floor((s % 3600) / 60);
    const seconds = s % 60;

    const pad = (num: number) => num.toString().padStart(2, '0');

    if (hours > 0) {
        return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
    } else {
        return `${pad(minutes)}:${pad(seconds)}`;
    }
}

export function getWeekdayDayAndMonth(date: Date): { weekday: string; dayAndMonth: string } {
    const weekday = date.toLocaleDateString('en-US', { weekday: 'long' });
    const dayAndMonth = date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
    return { weekday, dayAndMonth };
}

export function formatLongDate(date: Date | string): string {
    return new Date(date).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    });
}

export function formatShortDate(date: Date | string): string {
    return new Date(date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
}

export function formatNumericShortDate(date: Date | string): string {
    return new Date(date).toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: '2-digit',
    });
}

export function formatNumericDate(date: Date | string): string {
    return new Date(date).toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
    });
}

export function formatMonthDay(date: Date | string): string {
    return new Date(date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
    });
}

export function formatOrdinalDay(date: Date | string): string {
    const day = new Date(date).getDate();
    if (day >= 11 && day <= 13) return `${day}th`;
    const suffix = match(day % 10)
        .with(1, () => 'st')
        .with(2, () => 'nd')
        .with(3, () => 'rd')
        .otherwise(() => 'th');
    return `${day}${suffix}`;
}

export function formatNumericDateTime(date: Date | string): string {
    const d = new Date(date);
    return `${new Date(date).toLocaleDateString('en-US', {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric',
    })}, ${formatTime(d).toLowerCase()}`;
}

export function getLocalDayRange(date: Date): { start_date: string; end_date: string } {
    const start = new Date(date);
    start.setHours(0, 0, 0, 0);
    const end = new Date(date);
    end.setHours(23, 59, 59, 999);
    return {
        start_date: start.toISOString(),
        end_date: end.toISOString(),
    };
}

export function getStartOfDay(date: Date | string): Date {
    const start = new Date(date);
    start.setHours(0, 0, 0, 0);
    return start;
}

export function isToday(date: Date | string): boolean {
    return getStartOfDay(date).getTime() === getStartOfDay(new Date()).getTime();
}

export function formatLocalDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

export function parseLocalDate(value: string): Date {
    const [year, month, day] = value.slice(0, 10).split('-').map(Number);
    return new Date(year, month - 1, day);
}

export type LocalInstant = {
    date: Date;
    // false when minutesOfDay falls in a spring-forward gap and the JS engine rolls the wall
    // clock forward past it instead of refusing it.
    existsOnDate: boolean;
};

export function buildLocalInstant(date: string, minutesOfDay: number): LocalInstant {
    const instant = parseLocalDate(date);
    const requestedHours = Math.floor(minutesOfDay / 60);
    const requestedMinutes = minutesOfDay % 60;
    instant.setHours(requestedHours, requestedMinutes, 0, 0);

    const existsOnDate =
        instant.getHours() === requestedHours &&
        instant.getMinutes() === requestedMinutes &&
        formatLocalDate(instant) === date.slice(0, 10);

    return { date: instant, existsOnDate };
}

export function getNextWeekSameWeekday(): Date {
    const date = new Date();
    date.setDate(date.getDate() + 7);
    return getStartOfDay(date);
}

export function formatRelativeDays(date: Date | string): string {
    const relativeDate = getStartOfDay(date);
    const today = getStartOfDay(new Date());

    const diffInDays = Math.round(
        (relativeDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
    );

    return match(diffInDays)
        .with(P.number.lte(0), () => 'today')
        .with(1, () => 'in 1 day')
        .otherwise(() => `in ${diffInDays} days`);
}
